import {
	Alert,
	Box,
	Snackbar,
	Typography,
} from "@mui/material";
import { Button } from "@mui/material";
import { format } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { RegularShiftRequest } from "../../../../../features/worker-requests/store/worker-requests-types";
import { useDateFnsLocale } from "../../../../../utils/date-locale";
import MarkCalledOff from "../../../../full-time-shifts/ui/weekly-shift-view/shift-dialog/mark-called-off";
import useWorkerSchedulerData from "../../../controllers/use-worker-scheduler-data";
import {
  fetchRequestTypes, fetchWorkerCallOffs,
  resetUpdateWorkerAvailabilityStatus
} from "../../../store/worker-scheduler-slice";
import CallOffSectionSkeleton from "./call-off-section-skeleton";

interface CallOffSectionProps {
  workerId: string;
  workerFullName: string;
  currentAssignment: {
    job: string;
    workcenter: string;
    badgeNumber?: any;
    location?: any;
  } | null;
  shiftDetails: {
    shiftPatternName: string;
    startDateTime: string;
    endDateTime: string;
  } | null;
  fromStatus: string;
  onConfirm?: (callOffData: {
    ftRequestTypeId: number;
    ftLeaveTypes: {
      leaveTypeId: number;
      requestedHours: number;
    }[];
    ftSupervisorComment: string;
    ftStartDateTime: string;
    ftEndDateTime: string;
    ftFromStatus: string;
  }) => void;
  onConfirmEdit?: (callOffData: {
    ftShiftId?: number;
    workerId?: string;
    toStatusCode: string;
    callOffParams: {
      requestId?: number;
      requestTypeId: number;
      leaveTypes: { leaveTypeId: number; requestedHours: number }[];
      supervisorComment: string;
      startDate: string;
      endDate: string;
    };
  }) => void;
  onCancel: (() => void) | undefined;
  onBack?: () => void;
  initialData?: RegularShiftRequest;
  showFooter?: boolean;
  setCalledOffData?: React.Dispatch<
    React.SetStateAction<{
      ftRequestTypeId: number;
      ftLeaveTypes: {
        leaveTypeId: number;
        requestedHours: number;
      }[];
      ftSupervisorComment: string;
      ftStartDateTime: string;
      ftEndDateTime: string;
      ftFromStatus: string;
    } | null>
  >;
}

const sectionTitleStyle = {
	fontFamily: "Roboto",
	fontWeight: 400,
	fontSize: "14px",
	lineHeight: "20px",
	color: "#00000099",
	marginBottom: "4px",
};
const sectionValueStyle = {
	fontFamily: "Roboto",
	fontWeight: 400,
	fontSize: "16px",
	lineHeight: "24px",
	color: "#000000DE",
};

const CallOffSection = ({
  workerId,
	workerFullName,
	currentAssignment,
  shiftDetails,
  fromStatus,
	onConfirm,
  onConfirmEdit,
	onCancel,
	onBack,
  initialData,
  showFooter = true,
  setCalledOffData: setExternalCalledOffData,
}: CallOffSectionProps) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const dateLocale = useDateFnsLocale();
	const {
		fetchRequestTypesStatus,
		fetchWorkerCallOffsStatus,
		updateAvailabilityStatusStatus,
		updateAvailabilityStatusErrorMessage,
	} = useWorkerSchedulerData();

  const [internalCalledOffData, setInternalCalledOffData] = useState<{
    ftRequestTypeId: number;
    ftLeaveTypes: {
      leaveTypeId: number;
      requestedHours: number;
    }[];
    ftSupervisorComment: string;
    ftStartDateTime: string;
    ftEndDateTime: string;
    ftFromStatus: string;
  } | null>(null);

  const [isFormDirty, setIsFormDirty] = useState(false);

  const calledOffData = internalCalledOffData;

  const handleSetCalledOffData = useCallback((data: any) => {
    setInternalCalledOffData(data);
    if (setExternalCalledOffData) {
      setExternalCalledOffData(data);
    }
  }, [setExternalCalledOffData]);

  const handleIsDirtyChange = useCallback((dirty: boolean) => {
    setIsFormDirty(dirty);
  }, []);

	useEffect(() => {
		if (fetchRequestTypesStatus === "idle") {
			dispatch(fetchRequestTypes());
		}
	}, [dispatch, fetchRequestTypesStatus]);

	useEffect(() => {
		if (workerId) {
			dispatch(fetchWorkerCallOffs(workerId));
		}
	}, [workerId, dispatch]);

	const isLoading = useMemo(
		() =>
			fetchRequestTypesStatus === "pending" ||
			fetchWorkerCallOffsStatus === "pending",
		[fetchRequestTypesStatus, fetchWorkerCallOffsStatus]
	);

  const handleConfirm = useCallback(() => {
    const callOffParams = {
      ftRequestTypeId: calledOffData?.ftRequestTypeId ?? 0,
      ftLeaveTypes: calledOffData?.ftLeaveTypes ?? [],
      ftSupervisorComment: calledOffData?.ftSupervisorComment ?? "",
      ftStartDateTime: calledOffData?.ftStartDateTime ?? "",
      ftEndDateTime: calledOffData?.ftEndDateTime ?? "",
      ftFromStatus: fromStatus,
    };

    if (onConfirm) {
      onConfirm(callOffParams);
    }
  }, [calledOffData, onConfirm, fromStatus]);

  const handleConfirmEdit = useCallback(() => {
    const payload = {
      ...(initialData?.ftShiftId && { ftShiftId: initialData?.ftShiftId }),
      ...(initialData?.workerId && { workerId: initialData?.workerId }),
      callOffParams: {
        ...(initialData?.requestId && { requestId: initialData.requestId }),
        requestTypeId: calledOffData?.ftRequestTypeId ?? 0,
        leaveTypes: calledOffData?.ftLeaveTypes ?? [],
        supervisorComment: calledOffData?.ftSupervisorComment ?? "",
        startDate: calledOffData?.ftStartDateTime ?? "",
        endDate: calledOffData?.ftEndDateTime ?? "",
      },
      toStatusCode: "COF",
    };

    if (onConfirmEdit) {
      onConfirmEdit(payload);
    }
  }, [calledOffData, onConfirmEdit, initialData]);

  useEffect(() => {
    if (fetchRequestTypesStatus === "idle") {
      dispatch(fetchRequestTypes());
    }
  }, [dispatch, fetchRequestTypesStatus]);

  useEffect(() => {
    if (workerId) {
      dispatch(fetchWorkerCallOffs(workerId));
    }
  }, [workerId, dispatch]);

  useEffect(() => {
    if (updateAvailabilityStatusStatus === "fulfilled") {
      if (initialData && onCancel) {
        onCancel();
      }
    }
  }, [updateAvailabilityStatusStatus, initialData, onCancel]);

	if (isLoading) {
		return <CallOffSectionSkeleton />;
	}

	return (
    <>
      <Box>
        <Typography
          id="call-off-dialog-title"
          sx={{
            fontFamily: "Roboto",
            fontWeight: 500,
            fontSize: "20px",
            lineHeight: "32px",
            color: "#000000DE",
            padding: "45px 45px 16px 45px",
          }}
        >
          {initialData
            ? t("full-time-worker-scheduler_edit-time-off")
            : t("full-time-worker-scheduler_mark-as-time-off")}{" "}
          -{" "}
          {workerFullName}
        </Typography>
        <Box sx={{ padding: "0 45px 20px 45px" }}>
          {/* Shift Details Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: "24px",
              marginBottom: "20px",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography sx={sectionTitleStyle}>
                {t("full-time-worker-scheduler_shift-details")}
              </Typography>
              {shiftDetails && (
                <Typography sx={sectionValueStyle}>
                  {shiftDetails.shiftPatternName},{" "}
                  {format(
                    new Date(shiftDetails.startDateTime),
                    "EEE, d MMM h:mm a",
                    { locale: dateLocale }
                  )}{" "}
                  - {format(new Date(shiftDetails.endDateTime), "h:mm a")}
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={sectionTitleStyle}>
                {t("full-time-worker-scheduler_job-role-workcenter")}
              </Typography>
              {currentAssignment && (
                <Typography sx={sectionValueStyle}>
                  {currentAssignment.job} ({currentAssignment.workcenter})
                </Typography>
              )}
            </Box>
          </Box>
          <MarkCalledOff
            workerId={workerId}
            fromStatus={fromStatus}
            setCalledOffData={handleSetCalledOffData}
            shiftDetails={shiftDetails}
            initialData={initialData}
            setIsDirty={handleIsDirtyChange}
          />
        </Box>
        {showFooter && (
          <Box
            sx={{
              padding: "8px 45px 45px 45px",
              gap: "8px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              onClick={onCancel}
              variant="text"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0.1px",
                textTransform: "uppercase",
                minWidth: "64px",
                padding: "8px 16px",
              }}
            >
              {t("common_cancel")}
            </Button>
            {onBack && (
              <Button
                onClick={onBack}
                variant="outlined"
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "0.1px",
                  textTransform: "uppercase",
                  minWidth: "64px",
                  padding: "8px 16px",
                }}
              >
                {t("full-time-worker-scheduler_back")}
              </Button>
            )}
            <Button
              onClick={initialData ? handleConfirmEdit : handleConfirm}
              variant="contained"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0.1px",
                textTransform: "uppercase",
                minWidth: "160px",
                padding: "8px 16px",
              }}
              loading={updateAvailabilityStatusStatus === "pending"}
              disabled={
                updateAvailabilityStatusStatus === "pending" ||
                calledOffData === null ||
                calledOffData?.ftRequestTypeId === 0 ||
                calledOffData?.ftEndDateTime === "" ||
                calledOffData?.ftLeaveTypes?.length === 0 ||
                (!!initialData && !isFormDirty)
              }
            >
              {initialData
                ? t("full-time-worker-scheduler_update-time-off")
                : t("full-time-worker-scheduler_confirm-time-off")}
            </Button>
          </Box>
        )}
      </Box>
      {updateAvailabilityStatusStatus === "error" && (
        <Snackbar
          open={updateAvailabilityStatusStatus === "error"}
          autoHideDuration={2000}
          onClose={() => dispatch(resetUpdateWorkerAvailabilityStatus())}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert elevation={6} variant="filled" severity="error">
            {updateAvailabilityStatusErrorMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default CallOffSection;
